import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Api from "../../services/Api";

function navBtn() {
  return {
    background: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 32,
    height: 32,
    cursor: "pointer",
    fontSize: 20,
    color: "#E8956A",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    lineHeight: 1,
  };
}

function footerBtn(danger) {
  return {
    width: 48,
    height: 48,
    borderRadius: "50%",
    border: "none",
    background: danger ? "rgba(229,57,53,0.85)" : "rgba(255,255,255,0.15)",
    color: "#fff",
    cursor: "pointer",
    fontSize: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(8px)",
  };
}

// Carrossel de fotos do jazigo com suporte a arrastar e lightbox.
// Quando não há fotos cadastradas, exibe a imagem padrão /jazigo.jpg; se o
// usuário estiver logado, ela também abre o lightbox (só com a ação de adicionar).
function JazigoCarousel({ fotos, index, setIndex, setFotos, jazigoId, user }) {
  const [lightbox, setLightbox] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const dragStartX = useRef(null);
  const dragging = useRef(false);
  const lbDragStartX = useRef(null);
  const lbDragging = useRef(false);
  const fileInputRef = useRef(null);
  const addInputRef = useRef(null);

  const hasFotos = fotos.length > 0;
  const currentFoto = hasFotos ? fotos[index] : null;
  const interactive = hasFotos || !!user;
  const multi = hasFotos && fotos.length > 1;
  const imgSrc = hasFotos
    ? Api.getJazigoFotosUrl(currentFoto.arquivo_path)
    : "/jazigo.jpg";
  const imgAlt = hasFotos ? currentFoto.descricao || "Foto do jazigo" : "Jazigo";

  // Mantém index dentro dos limites e fecha o lightbox se a última foto for excluída
  useEffect(() => {
    if (fotos.length === 0) {
      setLightbox(false);
      return;
    }
    if (index > fotos.length - 1) setIndex(fotos.length - 1);
  }, [fotos, index, setIndex]);

  const prev = () => {
    setRotation(0);
    setActionError("");
    setIndex((i) => (i - 1 + fotos.length) % fotos.length);
  };

  const next = () => {
    setRotation(0);
    setActionError("");
    setIndex((i) => (i + 1) % fotos.length);
  };

  const handleUpdateClick = () => {
    setActionError("");
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !currentFoto) return;
    setBusy(true);
    setActionError("");
    try {
      const novo = await Api.uploadJazigoFoto(
        jazigoId,
        file,
        currentFoto.tipo,
        currentFoto.descricao,
      );
      await Api.deleteJazigoFoto(currentFoto.id);
      const atualizado = {
        id: novo.foto_id,
        jazigo_id: jazigoId,
        tipo: currentFoto.tipo,
        arquivo_path: novo.arquivo,
        descricao: currentFoto.descricao,
      };
      setFotos((prev) =>
        prev.map((f) => (f.id === currentFoto.id ? atualizado : f)),
      );
      setRotation(0);
    } catch (err) {
      setActionError(err.message || "Erro ao atualizar a foto.");
    } finally {
      setBusy(false);
    }
  };

  const handleAddClick = () => {
    setActionError("");
    addInputRef.current?.click();
  };

  const handleAddFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setActionError("");
    try {
      const novo = await Api.uploadJazigoFoto(jazigoId, file);
      const novaFoto = {
        id: novo.foto_id,
        jazigo_id: jazigoId,
        tipo: "",
        arquivo_path: novo.arquivo,
        descricao: "",
      };
      setIndex(fotos.length);
      setFotos((prev) => [...prev, novaFoto]);
      setRotation(0);
    } catch (err) {
      setActionError(err.message || "Erro ao adicionar foto.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = () => {
    if (!currentFoto) return;
    setActionError("");
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!currentFoto) return;
    setDeleteConfirmOpen(false);
    setBusy(true);
    setActionError("");
    try {
      await Api.deleteJazigoFoto(currentFoto.id);
      const novaLista = fotos.filter((f) => f.id !== currentFoto.id);
      setFotos(novaLista);
      setRotation(0);
      if (novaLista.length === 0) {
        setLightbox(false);
        setIndex(0);
      } else if (index > novaLista.length - 1) {
        setIndex(novaLista.length - 1);
      }
    } catch (err) {
      setActionError(err.message || "Erro ao excluir a foto.");
    } finally {
      setBusy(false);
    }
  };

  // Drag no carrossel
  const onPD = (e) => {
    if (!interactive) return;
    dragStartX.current = e.clientX ?? e.touches?.[0]?.clientX;
    dragging.current = false;
  };
  const onPM = (e) => {
    if (!interactive || dragStartX.current === null) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    if (Math.abs(x - dragStartX.current) > 8) dragging.current = true;
  };
  const onPU = (e) => {
    if (!interactive || dragStartX.current === null) return;
    const x = e.clientX ?? e.changedTouches?.[0]?.clientX;
    const delta = x - dragStartX.current;
    if (multi && dragging.current && Math.abs(delta) > 40) {
      delta < 0 ? next() : prev();
    } else if (!dragging.current) {
      setRotation(0);
      setLightbox(true);
    }
    dragStartX.current = null;
    dragging.current = false;
  };

  // Drag no lightbox
  const onLbPD = (e) => {
    lbDragStartX.current = e.clientX ?? e.touches?.[0]?.clientX;
    lbDragging.current = false;
  };
  const onLbPM = (e) => {
    if (lbDragStartX.current === null) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    if (Math.abs(x - lbDragStartX.current) > 8) lbDragging.current = true;
  };
  const onLbPU = (e) => {
    if (lbDragStartX.current === null) return;
    const x = e.clientX ?? e.changedTouches?.[0]?.clientX;
    const delta = x - lbDragStartX.current;
    if (lbDragging.current && Math.abs(delta) > 40) {
      delta < 0 ? next() : prev();
    }
    lbDragStartX.current = null;
    lbDragging.current = false;
  };

  // Fechar lightbox (ou o popup de confirmação, se estiver aberto) com Escape
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key !== "Escape") return;
      if (deleteConfirmOpen) setDeleteConfirmOpen(false);
      else setLightbox(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, deleteConfirmOpen]);

  // Fecha o popup de confirmação se o lightbox for fechado por outro caminho
  useEffect(() => {
    if (!lightbox) setDeleteConfirmOpen(false);
  }, [lightbox]);

  return (
    <div style={{ userSelect: "none" }}>
      {/* ── CARROSSEL ── */}
      <div
        style={{
          position: "relative",
          borderRadius: 10,
          overflow: "hidden",
          cursor: interactive ? (multi ? "grab" : "zoom-in") : "default",
        }}
        onMouseDown={onPD}
        onMouseMove={onPM}
        onMouseUp={onPU}
        onMouseLeave={() => {
          dragStartX.current = null;
          dragging.current = false;
        }}
        onTouchStart={onPD}
        onTouchMove={onPM}
        onTouchEnd={onPU}
      >
        <img
          src={imgSrc}
          alt={imgAlt}
          draggable={false}
          style={{
            width: "100%",
            maxHeight: 280,
            objectFit: "cover",
            display: "block",
            pointerEvents: "none",
          }}
        />
        {/* ícone de zoom */}
        {interactive && (
          <span
            style={{
              position: "absolute",
              bottom: 8,
              right: 10,
              background: "rgba(0,0,0,0.45)",
              color: "#fff",
              borderRadius: 4,
              fontSize: 13,
              padding: "2px 6px",
              pointerEvents: "none",
              lineHeight: 1.4,
            }}
          >
            ⤢
          </span>
        )}
      </div>

      {multi && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
            marginTop: 8,
          }}
        >
          <button onClick={prev} style={navBtn()}>
            ‹
          </button>

          <div style={{ display: "flex", gap: 6 }}>
            {fotos.map((_, i) => (
              <span
                key={i}
                onClick={() => setIndex(i)}
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  cursor: "pointer",
                  background: i === index ? "#E8956A" : "#ddd",
                  display: "inline-block",
                  transition: "background 0.2s",
                }}
              />
            ))}
          </div>

          <button onClick={next} style={navBtn()}>
            ›
          </button>
        </div>
      )}

      {/* ── LIGHTBOX ── */}
      {lightbox &&
        createPortal(
          <div
            onClick={() => setLightbox(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.92)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                maxWidth: "95vw",
              }}
              onMouseDown={multi ? onLbPD : undefined}
              onMouseMove={multi ? onLbPM : undefined}
              onMouseUp={multi ? onLbPU : undefined}
              onMouseLeave={() => {
                lbDragStartX.current = null;
                lbDragging.current = false;
              }}
              onTouchStart={multi ? onLbPD : undefined}
              onTouchMove={multi ? onLbPM : undefined}
              onTouchEnd={multi ? onLbPU : undefined}
            >
              <img
                src={imgSrc}
                alt={imgAlt}
                draggable={false}
                style={{
                  maxWidth: "95vw",
                  maxHeight: "85vh",
                  objectFit: "contain",
                  borderRadius: 8,
                  pointerEvents: "none",
                  display: "block",
                  transform: `rotate(${rotation}deg)`,
                  transition: "transform 0.25s ease",
                }}
              />

              {hasFotos && currentFoto.descricao && (
                <p
                  style={{
                    color: "#ddd",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    marginTop: 10,
                    textAlign: "center",
                  }}
                >
                  {currentFoto.descricao}
                </p>
              )}

              {user && busy && (
                <p
                  style={{
                    color: "#ddd",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 12,
                    margin: "10px 0 0",
                    textAlign: "center",
                  }}
                >
                  Aguarde...
                </p>
              )}

              {user && actionError && (
                <p
                  style={{
                    color: "#ff6b6b",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 12,
                    margin: "10px 0 0",
                    textAlign: "center",
                    maxWidth: "80vw",
                  }}
                >
                  {actionError}
                </p>
              )}

              {user && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleFileSelected}
                  />
                  <input
                    ref={addInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleAddFileSelected}
                  />
                </>
              )}

              <div
                style={{
                  position: "fixed",
                  bottom: 20,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 12,
                  padding: "10px 14px",
                  borderRadius: 30,
                  background: "rgba(0,0,0,0.65)",
                  backdropFilter: "blur(8px)",
                  zIndex: 10001,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {user && hasFotos && (
                  <button
                    onClick={handleDelete}
                    disabled={busy}
                    style={footerBtn(true)}
                    title="Excluir foto"
                  >
                    🗑
                  </button>
                )}

                {hasFotos && (
                  <button
                    onClick={() => setRotation((r) => r - 90)}
                    style={footerBtn()}
                  >
                    ↺
                  </button>
                )}

                {multi && (
                  <button onClick={prev} style={footerBtn()}>
                    ‹
                  </button>
                )}

                <button onClick={() => setLightbox(false)} style={footerBtn()}>
                  ✕
                </button>

                {multi && (
                  <button onClick={next} style={footerBtn()}>
                    ›
                  </button>
                )}

                {hasFotos && (
                  <button
                    onClick={() => setRotation((r) => r + 90)}
                    style={footerBtn()}
                  >
                    ↻
                  </button>
                )}

                {user && hasFotos && (
                  <button
                    onClick={handleUpdateClick}
                    disabled={busy}
                    style={footerBtn()}
                    title="Atualizar foto"
                  >
                    {busy ? "…" : "✎"}
                  </button>
                )}

                {user && (
                  <button
                    onClick={handleAddClick}
                    disabled={busy}
                    style={footerBtn()}
                    title="Adicionar foto"
                  >
                    {busy ? "…" : "+"}
                  </button>
                )}
              </div>

              {multi && (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginTop: 12,
                    }}
                  >
                    {fotos.map((_, i) => (
                      <span
                        key={i}
                        onClick={() => {
                          setRotation(0);
                          setIndex(i);
                        }}
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          cursor: "pointer",
                          background:
                            i === index ? "#E8956A" : "rgba(255,255,255,0.35)",
                          display: "inline-block",
                          transition: "background 0.2s",
                        }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}

      {/* ── POPUP DE CONFIRMAÇÃO DE EXCLUSÃO ── */}
      {deleteConfirmOpen &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10010,
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 14,
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                padding: "32px 36px",
                maxWidth: 380,
                width: "90%",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
              <h3
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#2d2d2d",
                  marginBottom: 10,
                }}
              >
                Excluir foto
              </h3>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 15,
                  color: "#666",
                  marginBottom: 24,
                }}
              >
                Deseja realmente excluir esta foto? Essa ação não pode ser
                desfeita.
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <button
                  onClick={() => setDeleteConfirmOpen(false)}
                  style={{
                    background: "#f0f0f0",
                    border: "none",
                    color: "#555",
                    borderRadius: 8,
                    padding: "10px 28px",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmDelete}
                  style={{
                    background: "#e53935",
                    border: "none",
                    color: "#fff",
                    borderRadius: 8,
                    padding: "10px 28px",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default JazigoCarousel;
